import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

/**
 * La jauge de stock, dessinée comme celle d'un tableau de bord de véhicule.
 *
 * Le choix n'est pas décoratif. Une barre de progression répond « combien de
 * pour cent »; un cadran répond « où j'en suis », ce qui est la question que
 * se pose un exploitant devant sa cuve. C'est aussi la seule forme que tout le
 * monde sait déjà lire, y compris quelqu'un qui n'a jamais utilisé de logiciel.
 *
 * La couleur suit l'état et rien d'autre: rouge sous le seuil bas, orange au
 * plafond, pétrole entre les deux. Un stock normal n'a aucune raison d'être
 * vert — le vert dirait « bien joué » là où il n'y a rien à féliciter.
 */

const RAYON = 52;
const CIRCONFERENCE = 2 * Math.PI * RAYON;
/** Un cadran ouvert vers le bas: 240° de course, 120° de vide. */
const COURSE = 240 / 360;
const PISTE = CIRCONFERENCE * COURSE;
/** Le tracé d'un cercle SVG démarre à 3 heures; on le recale en bas à gauche. */
const DEPART = 150;

const TEINTES = {
  bas: 'stroke-destructive',
  haut: 'stroke-flame',
  normal: 'stroke-primary',
};

const TEINTES_TEXTE = {
  bas: 'text-destructive',
  haut: 'text-flame-ink',
  normal: 'text-foreground',
};

const borner = (valeur, min = 0, max = 100) =>
  Math.min(max, Math.max(min, Number.isFinite(valeur) ? valeur : min));

const DUREE = 900;
const adoucir = (t) => 1 - Math.pow(1 - t, 3);

const mouvementReduit = () => {
  try {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch {
    return false;
  }
};

/** Valeur affichée: glisse de l'ancienne à la nouvelle (de 0 au premier affichage). */
const useNiveauAnime = (cible, actif) => {
  const [affiche, setAffiche] = useState(actif ? 0 : cible);
  const courant = useRef(actif ? 0 : cible);
  useEffect(() => {
    if (!actif) {
      courant.current = cible;
      setAffiche(cible);
      return undefined;
    }
    const depart = courant.current;
    const debut = performance.now();
    let image;
    const pas = (maintenant) => {
      const t = Math.min(1, (maintenant - debut) / DUREE);
      courant.current = depart + (cible - depart) * adoucir(t);
      setAffiche(courant.current);
      if (t < 1) image = requestAnimationFrame(pas);
    };
    image = requestAnimationFrame(pas);
    return () => cancelAnimationFrame(image);
  }, [cible, actif]);
  return affiche;
};

export const FuelGauge = ({
  /** Remplissage en pourcentage de la capacité. */
  level = 0,
  /** `bas` | `normal` | `haut` — l'état, pas la valeur. */
  etat = 'normal',
  /** Ce qui s'affiche sous le pourcentage, au centre du cadran. */
  legende,
  valeurLegende,
  className,
  /** Remplissage progressif; l'accueil ne l'active pas. Ignoré en mouvement réduit. */
  anime = false,
}) => {
  const animer = anime && !mouvementReduit();
  const cible = borner(Number(level));
  const niveau = useNiveauAnime(cible, animer);
  const rempli = PISTE * (niveau / 100);
  const teinte = TEINTES[etat] || TEINTES.normal;

  return (
    <div data-anime={animer ? 'true' : 'false'} className={cn('relative mx-auto w-[168px] max-w-full', className)}>
      <svg
        viewBox="0 0 120 120"
        className="w-full"
        role="img"
        aria-label={`Niveau de stock: ${Math.round(niveau)} pour cent`}
      >
        <g transform={`rotate(${DEPART} 60 60)`}>
          {/* La piste: elle dit jusqu'où la jauge peut monter. Sans elle, un
              stock à 10 % ressemble à un arc minuscule flottant dans le vide. */}
          <circle
            cx="60"
            cy="60"
            r={RAYON}
            fill="none"
            strokeWidth="12"
            strokeLinecap="round"
            className="stroke-muted"
            strokeDasharray={`${PISTE} ${CIRCONFERENCE}`}
          />
          <circle
            cx="60"
            cy="60"
            r={RAYON}
            fill="none"
            strokeWidth="12"
            strokeLinecap="round"
            className={cn(teinte, !animer && 'transition-[stroke-dasharray] duration-700 ease-out')}
            strokeDasharray={`${rempli} ${CIRCONFERENCE}`}
          />
        </g>
      </svg>

      {/* Le texte est posé par-dessus plutôt qu'en `<text>` SVG: il hérite
          ainsi des polices et des tailles du reste de l'interface, et reste
          sélectionnable. */}
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center pb-3 text-center">
        <span
          className={cn(
            'text-xl font-black leading-none tabular-nums',
            TEINTES_TEXTE[etat] || TEINTES_TEXTE.normal,
          )}
        >
          {Math.round(niveau)}%
        </span>
        {legende ? (
          <span className="mt-1.5 max-w-[8rem] text-[0.68rem] leading-tight text-muted-foreground">
            {legende}
          </span>
        ) : null}
        {valeurLegende ? (
          <span className="max-w-[9.5rem] text-[0.72rem] font-semibold leading-tight tabular-nums text-foreground">
            {valeurLegende}
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default FuelGauge;
